import React from 'react';
import { useLanguage } from '../context/LanguageContext';

const Pricing = () => {
    const { t } = useLanguage();

    const plans = [
        {
            name: t('pricing.plans.standard.name'),
            price: '₹1',
            period: t('pricing.period'),
            features: t('pricing.plans.standard.features') || [],
            popular: false
        },
        {
            name: t('pricing.plans.pro.name'),
            price: '₹1',
            period: t('pricing.period'),
            features: t('pricing.plans.pro.features') || [],
            popular: true
        },
        {
            name: t('pricing.plans.enterprise.name'),
            price: '$699',
            period: t('pricing.period'),
            features: t('pricing.plans.enterprise.features') || [],
            popular: false
        }
    ];

    const styles = {
        section: {
            padding: '100px 32px',
            backgroundColor: '#F9F9F9',
        },
        container: {
            maxWidth: '1400px',
            margin: '0 auto',
        },
        header: {
            textAlign: 'center',
            marginBottom: '60px',
        },
        h2: {
            fontSize: '48px',
            fontWeight: '800',
            marginBottom: '12px',
            color: '#2B2B2B',
        },
        highlight: {
            color: '#3457D5',
        },
        subtext: {
            fontSize: '20px',
            color: '#6E6E6E',
        },
        grid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '32px',
            marginBottom: '48px',
        },
        card: {
            backgroundColor: '#FFFFFF',
            padding: '40px',
            borderRadius: '24px',
            border: '1px solid #E5E5E5',
            position: 'relative',
            transition: 'all 0.3s',
        },
        popularCard: {
            border: '2px solid #3457D5',
            transform: 'scale(1.05)',
        },
        popularBadge: {
            position: 'absolute',
            top: '-12px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#3457D5',
            color: '#FFFFFF',
            padding: '6px 20px',
            borderRadius: '999px',
            fontSize: '12px',
            fontWeight: '700',
            letterSpacing: '0.5px',
        },
        planName: {
            fontSize: '28px',
            fontWeight: '700',
            color: '#2B2B2B',
            marginBottom: '12px',
        },
        price: {
            fontSize: '48px',
            fontWeight: '800',
            color: '#3457D5',
            marginBottom: '24px',
        },
        period: {
            fontSize: '20px',
            color: '#6E6E6E',
            fontWeight: '500',
        },
        featureList: {
            listStyle: 'none',
            padding: 0,
            margin: '0 0 32px 0',
        },
        featureItem: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            marginBottom: '12px',
            fontSize: '15px',
            color: '#414141',
            lineHeight: '1.6',
        },
        button: {
            width: '100%',
            padding: '16px',
            backgroundColor: '#FFFFFF',
            border: '2px solid #3457D5',
            color: '#3457D5',
            fontWeight: '600',
            borderRadius: '999px',
            cursor: 'pointer',
            fontSize: '16px',
            transition: 'all 0.3s',
        },
        primaryButton: {
            backgroundColor: '#D81F26',
            border: '2px solid #D81F26',
            color: '#FFFFFF',
        },
        note: {
            maxWidth: '900px',
            margin: '0 auto',
            backgroundColor: '#FFFFFF',
            padding: '32px',
            borderRadius: '20px',
            textAlign: 'center',
            border: '1px solid #E5E5E5',
        },
        noteText: {
            fontSize: '16px',
            color: '#414141',
            lineHeight: '1.7',
        },
        noteHighlight: {
            color: '#3457D5',
            fontWeight: '700',
        },
    };

    const isMobile = window.innerWidth < 768;
    if (isMobile) {
        styles.section.padding = '60px 20px';
        styles.h2.fontSize = '32px';
        styles.grid.gridTemplateColumns = '1fr';
        styles.popularCard.transform = 'scale(1)';
    }

    return (
        <section style={styles.section} id="pricing">
            <div style={styles.container}>
                <div style={styles.header}>
                    <h2 style={styles.h2}>
                        {t('pricing.title')}<span style={styles.highlight}>{t('pricing.titleHighlight')}</span>
                    </h2>
                    <p style={styles.subtext}>{t('pricing.subtext')}</p>
                </div>

                <div style={styles.grid}>
                    {plans.map((plan, index) => (
                        <div
                            key={index}
                            style={{ ...styles.card, ...(plan.popular && styles.popularCard) }}
                        >
                            {plan.popular && (
                                <div style={styles.popularBadge}>POPULAR</div>
                            )}

                            <h3 style={styles.planName}>{plan.name}</h3>
                            <div style={{ marginBottom: '24px' }}>
                                <span style={styles.price}>{plan.price}</span>
                                <span style={styles.period}>{plan.period}</span>
                            </div>

                            <ul style={styles.featureList}>
                                {plan.features.map((feature, fIndex) => (
                                    <li key={fIndex} style={styles.featureItem}>
                                        <svg style={{ width: '20px', height: '20px', flexShrink: 0, marginTop: '2px' }} fill="none" stroke="#3457D5" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        {feature}
                                    </li>
                                ))}
                            </ul>

                            <button
                                style={{ ...styles.button, ...(plan.popular && styles.primaryButton) }}
                                onMouseEnter={(e) => {
                                    if (plan.popular) {
                                        e.target.style.backgroundColor = '#B01820';
                                    } else {
                                        e.target.style.backgroundColor = '#3457D5';
                                        e.target.style.color = '#FFFFFF';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (plan.popular) {
                                        e.target.style.backgroundColor = '#D81F26';
                                    } else {
                                        e.target.style.backgroundColor = '#FFFFFF';
                                        e.target.style.color = '#3457D5';
                                    }
                                }}
                                onClick={() => window.location.href = `/dashboard?newOrder=true&plan=${encodeURIComponent(plan.name + ' License')}`}
                            >
                                {t('pricing.getStarted')}
                            </button>
                        </div>
                    ))}
                </div>

                <div style={styles.note}>
                    <p style={styles.noteText}>
                        {t('pricing.note')}<span style={styles.noteHighlight}>{t('pricing.noteHighlight1')}</span>
                        {t('pricing.noteMiddle')}<span style={styles.noteHighlight}>{t('pricing.noteHighlight2')}</span>
                        {t('pricing.noteEnd')}
                    </p>
                </div>
            </div>
        </section>
    );
};

export default Pricing;
